import * as Tooltip from "@radix-ui/react-tooltip";
import clsx from "clsx";
import React from "react";
import { FormattedMessage } from "react-intl";

import { CourseId } from "~/common/courses";
import { featureFlags } from "~/common/feature-flags";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "~/common/redesign-v1/ui/alert-dialog";
import { LabelledCheckbox } from "~/components/ui/labelled-checkbox";

export type AdditionalPaidServiceTag = "PRESCRIPTION_TRANSCRIBING" | "PRESCRIPTION_WITH_EXPERT" | "FAST_3D_PLAN" | "FAST_CAPS_PRINTING" | "LEAD_TECHNICIAN_3D_PLAN";

export const ADDITIONAL_PAID_SERVICE_TAGS: AdditionalPaidServiceTag[] = ["PRESCRIPTION_TRANSCRIBING", "PRESCRIPTION_WITH_EXPERT", "FAST_3D_PLAN", "FAST_CAPS_PRINTING", "LEAD_TECHNICIAN_3D_PLAN"];

const PRESCRIPTION_SERVICE_TAGS = new Set<AdditionalPaidServiceTag>(["PRESCRIPTION_TRANSCRIBING", "PRESCRIPTION_WITH_EXPERT"]);
export const EXPERT_SUPPORT_PRICE = 7_000;

const DISCONTINUED_ADDITIONAL_SERVICE_TAGS = new Set<AdditionalPaidServiceTag>(["FAST_3D_PLAN", "FAST_CAPS_PRINTING", "LEAD_TECHNICIAN_3D_PLAN"]);

export function getExpertSupportPrice(tags: AdditionalPaidServiceTag[] | string[] | null | undefined): number {
  return Array.isArray(tags) && tags.some((tag) => PRESCRIPTION_SERVICE_TAGS.has(tag as AdditionalPaidServiceTag)) ? EXPERT_SUPPORT_PRICE : 0;
}

export function isSmartPrescriptionFillingSelected(tags: AdditionalPaidServiceTag[] | string[] | null | undefined): boolean {
  // Feature flag gate: OFF → весь смарт-flow (аудио, координатор, блокировка и
  // авто-заполнение рецепта) не активируется, даже если тег как-то проставлен.
  if (!featureFlags.smartPrescription) {
    return false;
  }
  return normalizeAdditionalServiceTags(tags).some((tag) => PRESCRIPTION_SERVICE_TAGS.has(tag));
}

const COURSES_WITH_ADDITIONAL_SERVICES = new Set<CourseId>([
  CourseId.LIMITLESS,
  CourseId.TRANSPARENT,
  CourseId.FULL,
  CourseId.SHORT,
  CourseId.SUPER_SHORT,
  CourseId.SINGLE_ARCH,
  CourseId.TEEN,
  CourseId.CHILDREN,
  CourseId.CHILDREN_SHORT,
  CourseId.PLAN_3D,
]);

export const ADDITIONAL_SERVICE_PRICES: Record<AdditionalPaidServiceTag, string> = {
  PRESCRIPTION_TRANSCRIBING: "7 000 ₽",
  PRESCRIPTION_WITH_EXPERT: "7 000 ₽",
  FAST_3D_PLAN: "5 000 ₽",
  FAST_CAPS_PRINTING: "5 000 ₽",
  LEAD_TECHNICIAN_3D_PLAN: "3 000 ₽",
};

export function hasAdditionalServices(courseId: CourseId | null | undefined): boolean {
  return courseId != null && COURSES_WITH_ADDITIONAL_SERVICES.has(courseId);
}

function normalizeAdditionalServiceTags(tags: string[] | AdditionalPaidServiceTag[] | null | undefined): AdditionalPaidServiceTag[] {
  if (!Array.isArray(tags)) {
    return [];
  }

  const allowedTags = new Set<string>(ADDITIONAL_PAID_SERVICE_TAGS);
  return (tags as string[]).reduce<AdditionalPaidServiceTag[]>((acc, tag) => {
    if (allowedTags.has(tag)) {
      acc.push(tag as AdditionalPaidServiceTag);
    }
    return acc;
  }, []);
}

/**
 * Видимость чекбокса «Глубокий анализ КЛКТ» — единственное, что осталось в этом блоке,
 * когда `featureFlags.additionalPaidServices` выключен.
 *
 * Флаг ON: показываем, только если у курса вообще есть опция КЛКТ (`deep_cbct != null`).
 * Флаг OFF (36.2.12): на форме СОЗДАНИЯ условие было тем же, а на форме РЕДАКТИРОВАНИЯ
 * блок рендерился всегда, как только выбран курс, — это поведение и восстанавливаем.
 */
export function resolveShowDeepCBCT(courseHasCBCTOption: boolean, isUpdate = false): boolean {
  if (featureFlags.additionalPaidServices) {
    return courseHasCBCTOption;
  }
  return isUpdate || courseHasCBCTOption;
}

/**
 * Состояние чекбокса «Глубокий анализ КЛКТ».
 *
 * Флаг ON: галка стоит и тогда, когда КЛКТ уже входит в курс.
 * Флаг OFF (36.2.12): только явный выбор врача (`deep_cbct == true`).
 */
export function resolveDeepCBCTChecked(explicitlyChecked: boolean, includedInCourse: boolean): boolean {
  return featureFlags.additionalPaidServices ? explicitlyChecked || includedInCourse : explicitlyChecked;
}

export function PatientNewAdditionalServices({
  courseId,
  deepCBCTChecked,
  deepCBCTDisabled,
  showDeepCBCT,
  onDeepCBCTCheckedChange,
  additionalServiceTags,
  onAdditionalServiceTagsChange,
  isUpdate = false,
  compact = false,
  hideSmartPrescriptionTag = false,
}: {
  courseId: CourseId | null | undefined;
  deepCBCTChecked: boolean;
  deepCBCTDisabled?: boolean;
  showDeepCBCT: boolean;
  onDeepCBCTCheckedChange(checked: boolean): void;
  additionalServiceTags: AdditionalPaidServiceTag[] | string[] | null | undefined;
  onAdditionalServiceTagsChange(tags: AdditionalPaidServiceTag[]): void;
  isUpdate?: boolean;
  compact?: boolean;
  hideSmartPrescriptionTag?: boolean;
}) {
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const selectedAdditionalServiceTags = normalizeAdditionalServiceTags(additionalServiceTags);
  const shouldShowAdditionalServices = hasAdditionalServices(courseId);
  const deepCBCTLabelId = isUpdate ? "C_DEEP_CBCT_ANALYSIS_ITEM" : "C_DEEP_CBCT_ANALYSIS";

  React.useEffect(() => {
    // Флаг выключен → блока платных услуг нет вовсе, и чистить нечего: вызов
    // `onAdditionalServiceTagsChange` дёрнул бы `setDirty` и подсветил форму
    // «изменённой» на ровном месте.
    if (!featureFlags.additionalPaidServices) {
      return;
    }
    if (!shouldShowAdditionalServices && selectedAdditionalServiceTags.length > 0) {
      onAdditionalServiceTagsChange([]);
    }
  }, [onAdditionalServiceTagsChange, selectedAdditionalServiceTags.length, shouldShowAdditionalServices]);

  // Disabled controls do not dispatch `click`. `pointerdown` lets the wrapper open the explanation dialog
  // when the pointer is directly over the disabled checkbox.
  const handleDeepCBCTPointerDown = () => {
    if (deepCBCTDisabled) {
      setIsDialogOpen(true);
    }
  };

  const handleAdditionalServiceChange = (tag: AdditionalPaidServiceTag, checked: boolean) => {
    const nextTags = new Set(selectedAdditionalServiceTags);

    if (checked) {
      nextTags.add(tag);
    } else {
      nextTags.delete(tag);
    }

    if (checked && PRESCRIPTION_SERVICE_TAGS.has(tag)) {
      for (const existingTag of PRESCRIPTION_SERVICE_TAGS) {
        if (existingTag !== tag) {
          nextTags.delete(existingTag);
        }
      }
    }

    onAdditionalServiceTagsChange(Array.from(nextTags));
  };

  if (!featureFlags.additionalPaidServices) {
    // ─── 36.2.12 (`224f6a2b`) baseline, restored verbatim ────────────────────────
    // Флаг выключен → блок выглядит и ведёт себя ровно как в 36.2.12:
    // заголовок «Дополнительные инструкции», один чекбокс «Глубокий анализ КЛКТ»,
    // `onPointerDown` на всей `.form-group` (см. комментарий выше), без списка
    // платных услуг, без `id="additional-services-block-label"` (правило с 20px
    // отступом в patient-form.css появилось вместе с платным блоком) и без
    // `tw-*`-обёртки у подписи. Ничего не рендерим, если чекбокс не показывается —
    // это старое условие call-site'а.
    if (!showDeepCBCT) {
      return null;
    }

    return (
      <>
        <div className="form-group" data-legacy-additional-instructions onPointerDown={handleDeepCBCTPointerDown}>
          <h5 className="block" style={{ fontWeight: 900 }}>
            <FormattedMessage id="ADDITIONAL_INSTRUCTIONS" />
          </h5>

          <LabelledCheckbox
            label={<FormattedMessage id={deepCBCTLabelId} />}
            name="deep_cbct"
            disabled={deepCBCTDisabled}
            checked={deepCBCTChecked}
            onChange={(e) => onDeepCBCTCheckedChange(e.currentTarget.checked)}
          />
        </div>

        <DeepCBCTIncludedDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} />
      </>
    );
  }

  if (!showDeepCBCT && !shouldShowAdditionalServices) {
    return null;
  }

  return (
    <>
      <div className="form-group">
        {compact ? (
          <div className="tw-mb-2 tw-font-sans tw-text-sm tw-font-semibold tw-leading-5 tw-tracking-normal tw-text-foreground">
            <FormattedMessage id="additional.services.add_to_course" />
          </div>
        ) : (
          <h5 id="additional-services-block-label" className="block" style={{ fontWeight: 900 }}>
            <FormattedMessage id="additional.services.list.title" defaultMessage="Дополнительные услуги" />
          </h5>
        )}

        {/* The header's `margin-bottom` governs this gap (see `#additional-services-block-label`
            in patient-form.css): a `margin-top` here would just COLLAPSE against that h5 margin
            (CSS sibling-margin collapsing takes the max of the two, not the sum) and never win
            against the general h3/h4/h5 24px default, so the fix has to live on the heading's own
            override, same as `#payment-block-label` / `#instructions-block-label`.
            The two conditional rows below share ONE `gap-2` wrapper so showing BOTH restores
            their original 8px spacing instead of each row carrying its own margin. */}
        {showDeepCBCT || shouldShowAdditionalServices ? (
          <div className="tw-flex tw-flex-col tw-gap-2">
            {showDeepCBCT ? (
              <div onPointerDown={handleDeepCBCTPointerDown}>
                <LabelledCheckbox
                  label={
                    <span className="tw-font-normal tw-leading-5 tw-text-[#34495E]" style={{ fontFamily: "Inter, sans-serif", fontSize: 14 }}>
                      <FormattedMessage id={deepCBCTLabelId} />
                    </span>
                  }
                  name="deep_cbct"
                  disabled={deepCBCTDisabled}
                  checked={deepCBCTChecked}
                  onChange={(e) => onDeepCBCTCheckedChange(e.currentTarget.checked)}
                />
              </div>
            ) : null}

            {shouldShowAdditionalServices ? (
              <div className="tw-space-y-2.5">
                {ADDITIONAL_PAID_SERVICE_TAGS.filter(
                  (tag) =>
                    !DISCONTINUED_ADDITIONAL_SERVICE_TAGS.has(tag) &&
                    // Экспертное сопровождение скрыто, пока фича-флаг выключен.
                    (tag !== "PRESCRIPTION_TRANSCRIBING" || (featureFlags.smartPrescription && !hideSmartPrescriptionTag)),
                ).map((tag) => (
                  <LabelledCheckbox
                    key={tag}
                    label={<AdditionalServiceLabel tag={tag} />}
                    name={`additional_service_tag_${tag}`}
                    checked={selectedAdditionalServiceTags.includes(tag)}
                    onChange={(e) => handleAdditionalServiceChange(tag, e.currentTarget.checked)}
                  />
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <DeepCBCTIncludedDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} />
    </>
  );
}

/**
 * Uses the shared redesign `AlertDialog` rather than styling a separate Radix dialog.
 */
function DeepCBCTIncludedDialog({ open, onOpenChange }: { open: boolean; onOpenChange(open: boolean): void }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <FormattedMessage id="deep_cbct.included.title" />
          </AlertDialogTitle>
          <AlertDialogDescription>
            <FormattedMessage id="deep_cbct.included.message" />
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={() => onOpenChange(false)}>OK</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function AdditionalServiceLabel({ tag }: { tag: AdditionalPaidServiceTag }) {
  const price = ADDITIONAL_SERVICE_PRICES[tag];

  if (tag !== "PRESCRIPTION_TRANSCRIBING") {
    return (
      <span className="tw-font-normal tw-leading-5 tw-text-[#34495E]" style={{ fontFamily: "Inter, sans-serif", fontSize: 14 }}>
        <FormattedMessage id={tag} />
        {price ? ` \u2014 ${price}` : null}
      </span>
    );
  }

  return (
    <Tooltip.Provider delayDuration={350}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <span className="tw-inline-flex tw-cursor-help tw-items-center tw-gap-1 tw-font-normal tw-leading-5 tw-text-[#34495E]" style={{ fontFamily: "Inter, sans-serif", fontSize: 14 }}>
            <FormattedMessage id={tag} />
            {price ? ` \u2014 ${price}` : null}
            <i className="icon-question tw-text-sm tw-text-[#9099a6]" />
          </span>
        </Tooltip.Trigger>

        <Tooltip.Portal>
          <Tooltip.Content
            side="bottom"
            align="start"
            sideOffset={8}
            className={clsx(
              "tw-max-w-[420px] tw-rounded tw-border-[3px] tw-border-[#f0f0f0] tw-bg-[#f7f7f7] tw-p-4 tw-text-left tw-shadow-md",
              "motion-safe:tw-duration-300 motion-safe:tw-animate-in",
              "data-[state=delayed-open]:tw-fade-in-0",
              "data-[state=delayed-open]:tw-slide-in-from-top-1",
            )}
          >
            <p className="tw-m-0 tw-text-[15px] tw-font-semibold">
              <FormattedMessage id="additional.services.smart.tooltip.title" />
            </p>
            <p className="tw-m-0 tw-mt-2 tw-text-sm tw-leading-5">
              <FormattedMessage id="additional.services.smart.tooltip.subtitle" />
            </p>
            <p className="tw-m-0 tw-mt-3 tw-text-sm tw-font-semibold">
              <FormattedMessage id="additional.services.smart.tooltip.how_it_works" />
            </p>
            <p className="tw-m-0 tw-mt-2 tw-text-sm tw-leading-5">
              <FormattedMessage id="additional.services.smart.tooltip.description" />
            </p>
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}
